import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environment';
import { DataserviceService } from '../dataservice.service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SalesFlowService {
  private readonly fidiumBase = environment.fidiumUrl || environment.baseUrlMarketing;

  constructor(private http: HttpClient, private data: DataserviceService) { }

  private headers(requireAuth = true): HttpHeaders {
    let headers = new HttpHeaders().set('Content-Type', 'application/json');

    if (requireAuth) {
      const token = this.data.getAuthToken();
      const refreshToken = this.data.getRefreshToken();

      if (token && token !== 'null' && token !== 'undefined') {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
      if (refreshToken && refreshToken !== 'null' && refreshToken !== 'undefined') {
        headers = headers.set('Refresh', refreshToken);
      }
    }

    return headers;
  }

  locationLookup(address: string): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/location`,
      { address },
      { headers: this.headers() }
    );
  }

  serviceability(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/serviceability`,
      payload,
      { headers: this.headers() }
    );
  }

  updateCartSelection(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/updateCartV2`,
      payload,
      { headers: this.headers() }
    );
  }

  createOrUpdateCustomer(payload: any, isUpdate = false, requireAuth = true): Observable<any> {
    const endpoint = isUpdate ? 'fidium-customers/update' : 'fidium-customers/create';
    return this.http.post(`${this.fidiumBase}${endpoint}`, payload, {
      headers: this.headers(requireAuth)
    });
  }

  updateCart(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/updateCart`,
      payload,
      { headers: this.headers() }
    );
  }

  getCartInfo(cartId: string): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/getCartInfo`,
      { cartId },
      { headers: this.headers() }
    );
  }

  sendOtp(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/sendOtp`,
      payload,
      { headers: this.headers() }
    );
  }

  verifyOtp(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/verifyOtp`,
      payload,
      { headers: this.headers() }
    );
  }

  bookingOptions(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/bookingOptions`,
      payload,
      { headers: this.headers() }
    );
  }

  bookAppointment(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/bookAppointment`,
      payload,
      { headers: this.headers() }
    );
  }

  submitOrder(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/submitOrder`,
      payload,
      { headers: this.headers() }
    );
  }

  patchCartGeneric(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/patchCartGeneric`,
      payload,
      { headers: this.headers() }
    );
  }

  conversionLookUp(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/conversionLookUp`,
      payload,
      { headers: this.headers() }
    );
  }

  phoneLookup(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/phoneLookup`,
      payload,
      { headers: this.headers() }
    );
  }

  updateAddons(payload: any): Observable<any> {
    return this.http.patch(
      `${this.fidiumBase}fidium-work-order/updateAddons`,
      payload,
      { headers: this.headers() }
    );
  }

  removeAddons(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/remove-addons`,
      payload,
      { headers: this.headers() }
    );
  }

  getAgreement(cartId: string): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/get-agreement`,
      { cartId },
      { headers: this.headers() }
    );
  }

  agreement(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/agreement`,
      payload,
      { headers: this.headers() }
    );
  }

  actionService(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/actionservice`,
      payload,
      { headers: this.headers() }
    );
  }

  validateEmail(email: string): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/validateEmail`,
      { email },
      { headers: this.headers() }
    );
  }

  addressValidation(payload: any): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/addressValidation`,
      payload,
      { headers: this.headers() }
    );
  }

  submitExistingService(cartId: string): Observable<any> {
    return this.http.post(
      `${this.fidiumBase}fidium-work-order/existing-service`,
      { cartId },
      { headers: this.headers() }
    );
  }

  getPartnerAccess(affiliateUUID?: string): Observable<any> {
    const params = affiliateUUID ? `?affiliateUUID=${affiliateUUID}` : '';
    return this.http.get(
      `${this.fidiumBase}fidium-work-order/partner-access${params}`,
      { headers: this.headers() }
    );
  }

  createCustomerAndWorkOrderInKnockVia(payload: any): Observable<any> {
    const knockViaBase = environment.baseUrl;

    return this.http.post(
      `${knockViaBase}customers/create-from-web`,
      payload,
      { headers: this.headers() }
    );
  }

  createCustomerInMarketingFlow(payload: any): Observable<any> {
    const marketingBase = environment.baseUrlMarketing;

    return this.http.post(
      `${marketingBase}customers/create-from-web`,
      payload,
      { headers: this.headers(false) }
    );
  }
}
